import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Briefcase, GraduationCap, Award, ExternalLink, FileText, CheckCircle2, Mic } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { SubjectIcon } from "@/lib/subjects";
import { Progress } from "@/components/ui/progress";

const STATUS_CLS = { "Ready to schedule": "bg-[#C8DCCB] text-[#2C4A3E]", "Almost ready": "bg-[#F2D49B] text-[#5B3A1E]", "Keep practicing": "bg-[#F6D9D3] text-[#8A3B2E]", "Not started": "bg-[#F1EADB] text-stone-600" };

const GedCard = ({ g }) => {
  const done = g.units.filter((u) => u.done).length;
  return (
    <Link to={`/pathway/ged/${g.id}`} data-testid={`ged-card-${g.id}`} className="group rounded-3xl p-6 bg-[#F7F3EB] border border-[#E6DEC3] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]">
      <div className="flex justify-between">
        <span className="w-11 h-11 rounded-2xl grid place-items-center" style={{ background: g.color }}><SubjectIcon name={g.icon} className="w-5 h-5 text-white" /></span>
        <ArrowUpRight className="w-5 h-5 text-stone-400 group-hover:text-[#C87D53]" />
      </div>
      <h3 className="font-display text-xl text-[#2C4A3E] mt-5">{g.name}</h3>
      <p className="text-xs text-stone-500 mt-1">{g.minutes} min official test · {done}/{g.units.length} lessons</p>
      <Progress value={(100 * done) / g.units.length} className="h-1.5 mt-3" />
      <div className="flex items-center justify-between mt-4">
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_CLS[g.readiness.status]}`} data-testid={`ged-status-${g.id}`}>{g.readiness.status}</span>
        {g.readiness.pct != null && <span className="text-sm text-[#2C4A3E]" data-testid={`ged-readiness-${g.id}`}>{g.readiness.pct}% practice</span>}
      </div>
      {g.latest_exam && <p className="text-xs text-stone-600 mt-3" data-testid={`ged-latest-exam-${g.id}`}>Last full exam: <b className="text-[#2C4A3E]">{g.latest_exam.est_score}</b> est. · {g.latest_exam.band}</p>}
    </Link>
  );
};

const BusinessTrack = ({ biz, cert }) => {
  const done = biz.modules.filter((m) => m.done).length;
  return (
    <section className="mt-16" data-testid="business-track">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53] inline-flex items-center gap-2"><Briefcase className="w-4 h-4" /> Business Launch Track</span>
          <h2 className="font-display text-3xl text-[#2C4A3E] mt-2">Build the business only you can build</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/pathway/pitch" data-testid="open-pitch-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm"><Mic className="w-4 h-4" /> Pitch practice</Link>
          <Link to="/pathway/plan" data-testid="open-plan-builder-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm"><FileText className="w-4 h-4" /> Business plan ({biz.plan_filled}/{biz.plan_total})</Link>
          {cert && <Link to={`/certificate/${cert.id}`} data-testid="view-certificate-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D99B26] text-white text-sm"><Award className="w-4 h-4" /> My certificate</Link>}
        </div>
      </div>
      <p className="text-sm text-stone-500 mt-2">{done}/8 modules · finish all modules and every plan section to earn your certificate.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {biz.modules.map((m, i) => (
          <Link key={i} to={`/pathway/lesson/business/biz/${i}`} data-testid={`biz-module-${i}`} className="rounded-2xl p-5 bg-white/70 border border-[#E6DEC3] hover:border-[#2C4A3E] transition-colors">
            <div className="flex justify-between text-xs text-stone-500"><span>Module {i + 1}</span>{m.done && <CheckCircle2 className="w-4 h-4 text-[#6B9080]" />}</div>
            <h3 className="font-display text-lg text-[#2C4A3E] mt-2">{m.title}</h3>
            <p className="text-xs italic text-[#C87D53] mt-2">"{m.q}"</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function PathwayHome() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get("/pathway").then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e))); }, []);
  if (err) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-600" data-testid="pathway-error">{err}</div>;
  if (!d) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="pathway-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Career Pathway · {d.state}</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3 leading-[1.05]">Earn your GED.<br /><em className="text-[#C87D53] font-normal">Launch your own business.</em></h1>
      <p className="mt-5 text-stone-600 max-w-2xl">A mindful alternative to college: prepare for all four official GED tests, then learn step by step how to turn who you are into a real business.</p>
      <section className="mt-14" data-testid="ged-track">
        <div className="flex flex-wrap justify-between items-end gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53] inline-flex items-center gap-2"><GraduationCap className="w-4 h-4" /> GED Prep Track</span>
            <h2 className="font-display text-3xl text-[#2C4A3E] mt-2">Four tests, one diploma</h2>
          </div>
          <a href={d.ged_link} target="_blank" rel="noreferrer" data-testid="ged-register-link" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E6DEC3] text-sm text-[#2C4A3E] hover:bg-[#F1EADB]">
            Schedule the official GED at GED.com <ExternalLink className="w-4 h-4" /></a>
        </div>
        <p className="text-xs text-stone-500 mt-2">ConsciousLearn provides GED preparation. The official GED test is given by GED Testing Service.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">{d.ged.map((g) => <GedCard key={g.id} g={g} />)}</div>
      </section>
      <BusinessTrack biz={d.business} cert={d.certificate} />
    </div>
  );
}
